import { Fragment, useState } from "react";
import { Trans, i18n } from "../util/useT.ts";
import { TeamAbbrevText } from "../components/TeamAbbrevLink.tsx";
import { DataTable } from "../components/DataTable/index.tsx";
import { MoreLinks } from "../components/MoreLinks.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import { getCols } from "../util/getCols.ts";
import { useLocal } from "../util/local.ts";
import { formatPlayerName } from "../util/playerNames.ts";
import type { View } from "../../../../common/types.ts";
import { PLAYER } from "../../../../common/constants.ts";
import { wrappedAgeAtDeath } from "../components/AgeAtDeath.tsx";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import { orderBy } from "../../../../common/utils.ts";
import type { DataTableRow } from "../components/DataTable/index.tsx";
import { wrappedDraftAbbrev } from "../components/DraftAbbrev.tsx";
import { SkillsBlock } from "../components/SkillsBlock.tsx";
import { getCol } from "../util/getCol.ts";
import { createMobileReturnUrl } from "../util/mobilePageContext.ts";
import { wrappedPosition } from "../util/positionNames.tsx";
import { MobileTooltip } from "../components/MobileTooltip.tsx";
import {
  MobileActionableEmptyState,
  MobileArchiveSeasonNav,
  MobileContentHero,
  MobileExpertDisclosure,
  MobileStatRow,
} from "../mobile/mobilePrimitives.tsx";
import { MobileP3ShareCard } from "../mobile/MobileP3ShareExportActions.tsx";
import { draftHistoryDefaultStats, filterPolicyKeys } from "../mobile/displayPolicies.ts";
import { isCountHighlightRenderable } from "../mobile/statMeaningfulness.ts";
import {
  getDraftHistoryBestPick,
  getDraftHistoryBiggestSteal,
  getDraftHistoryClassSummary,
  getDraftHistoryUserTeamSummary,
  getDraftHistoryUnderperformer,
} from "./draftHistoryStory.ts";

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const createDraftHistoryPlayerHref = (pid: number, season: number | "all") =>
  createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
    activeTab: String(season),
    filterKey: "draftHistorySeason",
    filterValue: season,
    pageID: "draftHistory",
    scrollY: getMobileScrollY(),
  });

const Summary = ({
  players,
  season,
  summaryStat,
}: {
  players: View<"draftHistory">["players"];
  season: number | "all";
  summaryStat: View<"draftHistory">["summaryStat"];
}) => {
  const col = getCol(`stat:${summaryStat}`);
  const statText = (
    <MobileTooltip title={col.desc}>
      <span>{col.title}</span>
    </MobileTooltip>
  );

  const formatStat = (p: (typeof players)[number]) =>
    helpers.roundStat(p.careerStats[summaryStat], summaryStat);
  const formatDraft = (p: (typeof players)[number]) =>
    p.draft.round === 0
      ? i18n.t("draftHistory.undrafted", { ns: "ui", defaultValue: "undrafted" })
      : `${p.draft.round}-${p.draft.pick}`;

  const firstPick = players.find((p) => p.draft.round === 1 && p.draft.pick === 1);

  const mostStat = orderBy(players, (p) => p.careerStats[summaryStat], "desc").slice(0, 3);

  const summaryRows = [];

  if (firstPick) {
    summaryRows.push(
      <>
        <b>{i18n.t("draftHistory.firstPick", { ns: "ui", defaultValue: "1st Pick:" })}</b>{" "}
        <a href={createDraftHistoryPlayerHref(firstPick.pid, season)}>
          {formatPlayerName(firstPick.firstName, firstPick.lastName)}
        </a>{" "}
        ({formatStat(firstPick)} {statText})
      </>,
    );
  }

  const roys = players.filter((p) => p.awardCounts.roy > 0);
  if (roys.length > 0) {
    summaryRows.push(
      <>
        <b>
          {i18n.t("draftHistory.rookieOfYear", { ns: "ui", defaultValue: "Rookie of the Year:" })}
        </b>{" "}
        {roys.map((p, i) => (
          <Fragment key={p.pid}>
            <a href={createDraftHistoryPlayerHref(p.pid, season)}>
              {formatPlayerName(p.firstName, p.lastName, p.firstNameShort)}
            </a>{" "}
            ({formatStat(p)}, {formatDraft(p)}){i < roys.length - 1 ? ", " : null}
          </Fragment>
        ))}
      </>,
    );
  }

  if (mostStat.length > 0 && mostStat.some((p) => p.careerStats[summaryStat] !== 0)) {
    summaryRows.push(
      <>
        <b>
          {i18n.t("draftHistory.most", { ns: "ui", defaultValue: "Most" })} {col.desc}:
        </b>{" "}
        {mostStat.map((p, i) => (
          <Fragment key={p.pid}>
            <a href={createDraftHistoryPlayerHref(p.pid, season)}>
              {formatPlayerName(p.firstName, p.lastName, p.firstNameShort)}
            </a>{" "}
            ({formatStat(p)}, {formatDraft(p)}){i < mostStat.length - 1 ? ", " : null}
          </Fragment>
        ))}
      </>,
    );
  }

  type AwardKey = "allStar" | "mvp" | "champ" | "hof";

  const [expanded, setExpanded] = useState<AwardKey[]>([]);

  const awards: {
    key: AwardKey;
    title: string;
  }[] = [
    {
      key: "champ",
      title: i18n.t("draftHistory.champion", { ns: "ui", defaultValue: "Champion" }),
    },
    {
      key: "allStar",
      title: i18n.t("draftHistory.allStar", { ns: "ui", defaultValue: "All-Star" }),
    },
    {
      key: "mvp",
      title: i18n.t("draftHistory.mvp", { ns: "ui", defaultValue: "MVP" }),
    },
    {
      key: "hof",
      title: i18n.t("draftHistory.hallOfFamer", { ns: "ui", defaultValue: "Hall of Famer" }),
    },
  ];
  for (const { key, title } of awards) {
    const filtered = orderBy(
      players.filter((p) => p.awardCounts[key] > 0),
      [(p) => p.awardCounts[key], (p) => p.careerStats[summaryStat]],
      ["desc", "desc"],
    );
    const count = filtered.length;
    if (count > 0) {
      const display = expanded.includes(key) ? filtered : filtered.slice(0, count === 4 ? 4 : 3);
      const excess = count - display.length;

      summaryRows.push(
        <>
          <b>
            {count} {helpers.plural(title, count)}
          </b>{" "}
          {display.map((p, i) => (
            <Fragment key={p.pid}>
              <a href={createDraftHistoryPlayerHref(p.pid, season)}>
                {formatPlayerName(p.firstName, p.lastName, p.firstNameShort)}
              </a>{" "}
              ({key !== "hof" ? `${p.awardCounts[key]}x, ` : null}
              {formatDraft(p)}){i < display.length - 1 ? ", " : null}
            </Fragment>
          ))}
          {excess > 0 ? (
            <>
              ,{" "}
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  setExpanded([...expanded, key]);
                }}
              >
                {i18n.t("draftHistory.moreCount", {
                  ns: "ui",
                  defaultValue: "{{count}} more",
                  count: excess,
                })}
              </a>
            </>
          ) : null}
        </>,
      );
    } else {
      summaryRows.push(
        <>
          <b>
            {i18n.t("draftHistory.zeroAwards", {
              ns: "ui",
              defaultValue: "0 {{titles}}",
              titles: `${title}s`,
            })}
          </b>
        </>,
      );
    }
  }

  return (
    <p>
      {summaryRows.map((row, i) => (
        <Fragment key={i}>
          {i > 0 ? <br /> : null}
          {row}
        </Fragment>
      ))}
    </p>
  );
};

const DraftHistory = ({
  challengeNoRatings,
  draftType,
  players,
  season,
  stats,
  summaryStat,
  teamsByTid,
  userTid,
}: View<"draftHistory">) => {
  const noDraft = draftType === "freeAgents";

  useTitleBar({
    title: noDraft
      ? i18n.t("moreLinks.Prospects_History", { ns: "ui", defaultValue: "Prospects History" })
      : i18n.t("menu.Draft History", { ns: "ui", defaultValue: "Draft History" }),
    jumpTo: !window.mobile,
    jumpToSeason: season,
    dropdownView: "draft_history",
    dropdownFields: { seasonsAndOldDrafts: season },
  });

  // B3 column convergence: on mobile the archive table keeps the 12-column
  // player budget (Name + draft identity + a career-stat subset); the
  // current/peak rating blocks and their ambiguous tri-"Ovr" superCol labels
  // stay desktop-only. Desktop keeps every column.
  const useMobileColumnBudget = !!window.mobile;
  const visibleStats = useMobileColumnBudget
    ? filterPolicyKeys([...stats], draftHistoryDefaultStats)
    : stats;

  const superCols = useMobileColumnBudget
    ? undefined
    : [
        {
          title: "",
          colspan: 3,
        },
        {
          title: noDraft
            ? i18n.t("draftHistory.asProspect", { ns: "ui", defaultValue: "As Prospect" })
            : i18n.t("draftHistory.atDraft", { ns: "ui", defaultValue: "At Draft" }),
          colspan: 5,
        },
        {
          title: i18n.t("draftHistory.current", { ns: "ui", defaultValue: "Current" }),
          colspan: 5,
        },
        {
          title: i18n.t("draftHistory.peak", { ns: "ui", defaultValue: "Peak" }),
          colspan: 4,
        },
        {
          title: i18n.t("hallOfFame.careerStats", { ns: "ui", defaultValue: "Career Stats" }),
          colspan: stats.length,
        },
      ];

  const cols = getCols(
    useMobileColumnBudget
      ? [
          "Name",
          "Pick",
          "Pos",
          "Team",
          "Age",
          "Ovr",
          "Pot",
          ...visibleStats.map((stat) => `stat:${stat}`),
        ]
      : [
          "Pick",
          "Name",
          "Pos",
          "Team",
          "Age",
          "Ovr",
          "Pot",
          "Skills",
          "Team",
          "Age",
          "Ovr",
          "Pot",
          "Skills",
          "Age",
          "Ovr",
          "Pot",
          "Skills",
          ...visibleStats.map((stat) => `stat:${stat}`),
        ],
  );

  const teamInfoCache = useLocal((state) => state.teamInfoCache);
  const storyStat = getCol(`stat:${summaryStat}`);
  const classSummary = getDraftHistoryClassSummary(players, summaryStat);
  const userTeamSummary = getDraftHistoryUserTeamSummary(players, userTid, summaryStat);
  const bestPick = getDraftHistoryBestPick(players, summaryStat);
  const biggestSteal = getDraftHistoryBiggestSteal(players, summaryStat);
  const underperformer = getDraftHistoryUnderperformer(players, summaryStat);
  const unavailableLabel = i18n.t("common.unavailable", {
    ns: "ui",
    defaultValue: "Unavailable",
  });
  const noStandoutLabel = i18n.t("draftHistory.mobileNoStandoutYet", {
    ns: "ui",
    defaultValue: "No standout yet",
  });
  const formatStoryStat = (p: (typeof players)[number] | undefined) =>
    p
      ? `${helpers.roundStat(p.careerStats[summaryStat], summaryStat)} ${storyStat.title}`
      : unavailableLabel;
  const formatStoryPlayerName = (p: (typeof players)[number] | undefined) =>
    p ? formatPlayerName(p.firstName, p.lastName, p.firstNameShort) : noStandoutLabel;
  const renderStoryPlayer = (p: (typeof players)[number] | undefined) =>
    p ? (
      <a href={createDraftHistoryPlayerHref(p.pid, season)}>
        {formatPlayerName(p.firstName, p.lastName, p.firstNameShort)}
      </a>
    ) : (
      <span className="text-body-secondary">{noStandoutLabel}</span>
    );
  const renderDraftSlot = (p: (typeof players)[number] | undefined) =>
    p
      ? p.draft.round === 0
        ? i18n.t("draftHistory.undrafted", {
            ns: "ui",
            defaultValue: "undrafted",
          })
        : i18n.t("draftHistory.mobilePickSlot", {
            ns: "ui",
            defaultValue: "Pick {{round}}-{{pick}}",
            pick: p.draft.pick,
            round: p.draft.round,
          })
      : unavailableLabel;
  const p3DraftResultShareTitle = i18n.t("draftHistory.p3ShareTitle", {
    ns: "ui",
    defaultValue: "{{season}} Draft Result",
    season,
  });
  const p3DraftResultShareText = classSummary.hasCareerStats
    ? i18n.t("draftHistory.p3ShareText", {
        ns: "ui",
        defaultValue:
          "{{players}} players. Best pick: {{bestPick}}. Biggest steal: {{biggestSteal}}. Top-pick miss: {{underperformer}}.",
        bestPick: formatStoryPlayerName(bestPick),
        biggestSteal: formatStoryPlayerName(biggestSteal),
        players: classSummary.players,
        underperformer: formatStoryPlayerName(underperformer),
      })
    : i18n.t("draftHistory.p3ShareFallbackText", {
        ns: "ui",
        defaultValue: "Draft class archive is available, but career production is not ready yet.",
      });
  const previousSeason = season - 1;
  const nextSeason = season + 1;
  const recentCompleteHref = helpers.leagueUrl(["draft_history"]);
  const draftCenterHref = helpers.leagueUrl(["draft"]);
  const p2s06DraftHistorySeasonTitle = noDraft
    ? i18n.t("draftHistory.p2s06ProspectHistoryTitle", {
        ns: "ui",
        defaultValue: "{{season}} prospect history",
        season,
      })
    : i18n.t("draftHistory.p2s06DraftHistoryTitle", {
        ns: "ui",
        defaultValue: "{{season}} draft history",
        season,
      });
  // G2b-2 fix: the empty state said "not complete" three times (title +
  // simplified reason with its "stays inside the draft archive" structure
  // narration + the legacy reason that also repeated the CTA, 法则一/法则二).
  // Converged to the standard shape: season title, one plain reason, one
  // impact line, one primary action.
  const p2s04DraftHistoryEmptyReason = i18n.t(
    "draftHistory.p2s04SimplifiedEmptyReason",
    {
      ns: "ui",
      defaultValue: "This draft is not complete yet.",
    },
  );
  const p2s06RecentCompleteDraftLabel = i18n.t("draftHistory.p2s06ViewRecentCompleteDraft", {
    ns: "ui",
    defaultValue: "View recent completed draft",
  });
  const archiveSeasonNav = (
    <MobileArchiveSeasonNav
      aria-label={i18n.t("draftHistory.mobileSeasonNavLabel", {
        ns: "ui",
        defaultValue: "Draft history seasons",
      })}
      className="mb-3"
      currentLabel={i18n.t("draftHistory.mobileViewingSeason", {
        ns: "ui",
        defaultValue: "Viewing",
      })}
      currentValue={season}
      nextHref={helpers.leagueUrl(["draft_history", nextSeason])}
      nextLabel={i18n.t("draftHistory.mobileNextSeason", {
        ns: "ui",
        defaultValue: "Next {{season}}",
        season: nextSeason,
      })}
      previousHref={helpers.leagueUrl(["draft_history", previousSeason])}
      previousLabel={i18n.t("draftHistory.mobilePreviousSeason", {
        ns: "ui",
        defaultValue: "Prev {{season}}",
        season: previousSeason,
      })}
      rootHref={recentCompleteHref}
      rootLabel={p2s06RecentCompleteDraftLabel}
    />
  );
  const draftHistorySecondaryDepth = (
    <MobileExpertDisclosure
      className="mt-3"
      expertHostId="draft-history-season-nav"
      open={false}
      summary={i18n.t("draftHistory.p2s04SeasonBrowseSheet", {
        ns: "ui",
        defaultValue: "Browse draft years",
      })}
    >
      {archiveSeasonNav}
      <div
        className="d-flex flex-column gap-1"
      >
        <a
          className="btn btn-outline-secondary"
          href={draftCenterHref}
        >
          {i18n.t("draftHistory.p2eReturnDraftCenter", {
            ns: "ui",
            defaultValue: "Return to draft center",
          })}
        </a>
      </div>
    </MobileExpertDisclosure>
  );

  if (players.length === 0) {
    return (
      <>
        <MoreLinks type="draft" page="draft_history" draftType={draftType} season={season} />
        <section
        >
          {/* G2b-2 fix: "not complete" appeared three times on one screen
              (incomplete title + narration reason + hidden legacy reason).
              The season title moves into the empty-state title slot (the
              standalone h2 duplicated it), the reason states the fact once,
              and the legacy narration span is gone. */}
          <MobileActionableEmptyState
            action={
              <a
                className="btn btn-primary"
                href={recentCompleteHref}
              >
                {p2s06RecentCompleteDraftLabel}
              </a>
            }
            impact={i18n.t("draftHistory.mobileEmptyImpact", {
              ns: "ui",
              defaultValue:
                "The full draft class fills in here once the draft is complete.",
            })}
            reason="not-started"
            reasonText={
              <span
              >
                {p2s04DraftHistoryEmptyReason}
              </span>
            }
            title={p2s06DraftHistorySeasonTitle}
          />
        </section>
        <div>
          {draftHistorySecondaryDepth}
        </div>
      </>
    );
  }

  const rows: DataTableRow[] = players.map((p) => {
    const showRatings = !challengeNoRatings || p.currentTid === PLAYER.RETIRED;

    return {
      key: p.pid,
      metadata: {
        type: "player",
        pid: p.pid,
        season: {
          export: season,
          default: "career",
        },
        playoffs: "regularSeason",
      },
      data: (() => {
        const pickCell = p.draft.round >= 1 ? `${p.draft.round}-${p.draft.pick}` : null;
        const nameCell = wrappedPlayerNameLabels({
          awards: p.awards,
          pid: p.pid,
          season,
          defaultWatch: p.watch,
          firstName: p.firstName,
          firstNameShort: p.firstNameShort,
          lastName: p.lastName,
          nameOnly: true,
          playerHref: createDraftHistoryPlayerHref(p.pid, season),
        });
        const posCell = wrappedPosition(p.pos);
        const draftTeamCell = wrappedDraftAbbrev(
          {
            originalTid: p.draft.originalTid,
            tid: p.draft.tid,
            originalT: teamsByTid[p.draft.originalTid],
            t: teamsByTid[p.draft.tid],
          },
          teamInfoCache,
        );
        const careerStatCells = visibleStats.map((stat) =>
          helpers.roundStat(p.careerStats[stat], stat),
        );

        if (useMobileColumnBudget) {
          return [
            nameCell,
            pickCell,
            posCell,
            draftTeamCell,
            p.draft.age,
            showRatings ? p.draft.ovr : null,
            showRatings ? p.draft.pot : null,
            ...careerStatCells,
          ];
        }

        return [
          pickCell,
          nameCell,
          posCell,
          draftTeamCell,
          p.draft.age,
          showRatings ? p.draft.ovr : null,
          showRatings ? p.draft.pot : null,
          <span className="skills-alone">
            <SkillsBlock host="draft-history-draft" skills={p.draft.skills} />
          </span>,
          <a
            href={createMobileCrossTabHref(
              helpers.leagueUrl(["roster", `${p.currentAbbrev}_${p.currentTid}`]),
            )}
          >
            <TeamAbbrevText tid={p.currentTid} abbrev={p.currentAbbrev} />
          </a>,
          wrappedAgeAtDeath(p.currentAge, p.ageAtDeath),
          showRatings ? p.currentOvr : null,
          showRatings ? p.currentPot : null,
          <span className="skills-alone">
            <SkillsBlock host="draft-history-current" skills={p.currentSkills} />
          </span>,
          p.peakAge,
          showRatings ? p.peakOvr : null,
          showRatings ? p.peakPot : null,
          <span className="skills-alone">
            <SkillsBlock host="draft-history-peak" skills={p.peakSkills} />
          </span>,
          ...careerStatCells,
        ];
      })(),
      classNames: {
        "table-danger": p.hof,
        "table-info": p.draft.tid === userTid,
      },
    };
  });

  return (
    <>
      <MoreLinks type="draft" page="draft_history" draftType={draftType} season={season} />

      <MobileContentHero
        className="mb-3"
        data-recent-class-summary=""
        // The class summary is the single explainer line (natural-zero copy on
        // the same span); the ArchiveHero surface + "Draft class story" eyebrow
        // narration are gone (法则一).
        explainer={
          <span
            data-natural-zero-copy={
              classSummary.hasCareerStats ? "has-career-stats" : "zero-career-stats"
            }
          >
            {classSummary.hasCareerStats
              ? i18n.t("draftHistory.mobileClassSummary", {
                  ns: "ui",
                  defaultValue:
                    "{{players}} players, {{awardWinners}} award winners, {{hallOfFamers}} Hall of Famers, and {{undrafted}} undrafted stories.",
                  awardWinners: classSummary.awardWinners,
                  hallOfFamers: classSummary.hallOfFamers,
                  players: classSummary.players,
                  undrafted: classSummary.undrafted,
                })
              : i18n.t("draftHistory.mobileClassNoCareerStats", {
                  ns: "ui",
                  defaultValue:
                    "This class has no career production yet, so the archive stays in setup mode.",
                })}
          </span>
        }
        title={
          <span
          >
            {p2s06DraftHistorySeasonTitle}
          </span>
        }
      >
        {/* The draft-class story conclusions (best pick / biggest steal /
            top-pick miss, draftHistoryStory.ts) are the first-screen content as
            MobileStatRow rows, InsightStrip → StatRow per b1 map. */}
        <MobileStatRow
          detail={formatStoryStat(bestPick)}
          label={i18n.t("draftHistory.mobileBestPick", {
            ns: "ui",
            defaultValue: "Best pick",
          })}
          tone="positive"
          value={<span data-best-pick="">{renderStoryPlayer(bestPick)}</span>}
        />
        <MobileStatRow
          detail={
            biggestSteal
              ? i18n.t("draftHistory.mobileStealDetail", {
                  ns: "ui",
                  defaultValue: "{{slot}}, {{stat}}",
                  slot: renderDraftSlot(biggestSteal),
                  stat: formatStoryStat(biggestSteal),
                })
              : unavailableLabel
          }
          label={i18n.t("draftHistory.mobileBiggestSteal", {
            ns: "ui",
            defaultValue: "Biggest steal",
          })}
          tone="positive"
          value={<span data-biggest-steal="">{renderStoryPlayer(biggestSteal)}</span>}
        />
        <MobileStatRow
          detail={
            underperformer
              ? i18n.t("draftHistory.mobileUnderperformerDetail", {
                  ns: "ui",
                  defaultValue: "{{slot}}, {{stat}}",
                  slot: renderDraftSlot(underperformer),
                  stat: formatStoryStat(underperformer),
                })
              : unavailableLabel
          }
          label={i18n.t("draftHistory.mobileUnderperformer", {
            ns: "ui",
            defaultValue: "Top-pick miss",
          })}
          tone="warning"
          value={<span data-underperformer="">{renderStoryPlayer(underperformer)}</span>}
        />
        {/* A3: count-0 highlight cards do not render */}
        {isCountHighlightRenderable(userTeamSummary.picks) ? (
          <MobileStatRow
            detail={
              userTeamSummary.bestPick
                ? formatStoryStat(userTeamSummary.bestPick)
                : i18n.t("draftHistory.mobileUserTeamNoProduction", {
                    ns: "ui",
                    defaultValue: "No career production yet.",
                  })
            }
            label={i18n.t("draftHistory.mobileUserTeamPicks", {
              ns: "ui",
              defaultValue: "Your picks",
            })}
            tone="positive"
            value={
              <span
              >
                {i18n.t("draftHistory.mobileUserTeamPickCount", {
                  ns: "ui",
                  defaultValue: "{{count}} picks",
                  count: userTeamSummary.picks,
                })}
              </span>
            }
          />
        ) : null}
        <MobileStatRow
          detail={storyStat.desc}
          label={i18n.t("draftHistory.mobileClassSize", {
            ns: "ui",
            defaultValue: "Class size",
          })}
          value={players.length}
        />
      </MobileContentHero>

      <details className="mb-3" data-native-details-host="draft-history-share">
        <summary className="btn btn-link btn-sm p-0">
          {i18n.t("draftHistory.p3ShareSummary", {
            ns: "ui",
            defaultValue: "Share draft result",
          })}
        </summary>
        <MobileP3ShareCard
          className="mt-2"
          kind="draftResult"
          payload={{
            text: p3DraftResultShareText,
            title: p3DraftResultShareTitle,
            url: helpers.leagueUrl(["draft_history", season]),
          }}
        />
      </details>

      <Summary key={season} players={players} season={season} summaryStat={summaryStat} />

      <p>
        <Trans
          i18nKey="draftHistory.draftedHighlights"
          ns="ui"
          defaults="Players drafted by your team are <blue>highlighted in blue</blue>. Players in the Hall of Fame are <red>highlighted in red</red>."
          components={{
            blue: <span className="text-info" />,
            red: <span className="text-danger" />,
          }}
        />
      </p>

      <MobileExpertDisclosure
        className="mb-3"
        count={i18n.t("draftHistory.mobileAdvancedTableCount", {
          ns: "ui",
          defaultValue: "{{count}} players",
          count: players.length,
        })}
        data-advanced-table-depth=""
        data-hidden-ratings-state={challengeNoRatings ? "hidden" : "visible"}
        expertHostId="draft-history-advanced-table"
        open={!window.mobile}
        summary={i18n.t("dataTable.advancedTable", {
          ns: "ui",
          defaultValue: "Advanced table",
        })}
      >
        {/* B3: the DataTable is the single archive-depth carrier — the pruned
            card list on mobile, the unchanged table on desktop. The
            duplicate summary copy is gone (法则二 / DataTable mobile exit). */}
        <DataTable
          cols={cols}
          defaultSort={[useMobileColumnBudget ? 1 : 0, "asc"]}
          defaultStickyCols={window.mobile ? 0 : 2}
          name="DraftHistory"
          rows={rows}
          superCols={superCols}
        />
      </MobileExpertDisclosure>
      <div>
        {draftHistorySecondaryDepth}
      </div>
    </>
  );
};

export default DraftHistory;
